import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import { CustomService } from "../../../services/api/custom/CustomService";
import { CepService } from "../../../services/api/cep/CepService";
import { toast } from "react-toastify";

// Lista de estados do Brasil
const estadosBR = [
  { value: "AC", label: "Acre" },
  { value: "AL", label: "Alagoas" },
  { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" },
  { value: "BA", label: "Bahia" },
  { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" },
  { value: "ES", label: "Espírito Santo" },
  { value: "GO", label: "Goiás" },
  { value: "MA", label: "Maranhão" },
  { value: "MT", label: "Mato Grosso" },
  { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" },
  { value: "PA", label: "Pará" },
  { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" },
  { value: "PE", label: "Pernambuco" },
  { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" },
  { value: "RN", label: "Rio Grande do Norte" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" },
  { value: "RR", label: "Roraima" },
  { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" },
  { value: "SE", label: "Sergipe" },
  { value: "TO", label: "Tocantins" },
];

function RegisterClienteForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      estado: "PE",
      telefoneContato: "",
      cnpj: "",
      cep: "",
    },
  });

  const cepValue = watch("cep");

  // Preenche dados existentes
  useEffect(() => {
    if (!isEdit) return;
    CustomService.getRequest(`/interno/cliente/${id}`)
      .then((resp) => {
        setValue("cnpj", resp.cnpj || "");
        setValue("razaoSocial", resp.razao_social || "");
        setValue("nomeResponsavel", resp.nome_responsavel || "");
        setValue("telefoneContato", resp.telefone_contato || "");
        setValue("observacao", resp.observacao || "");
        setValue("parceiro", resp.parceiro || false);
        if (resp.endereco) {
          setValue("cep", resp.endereco.cep || "");
          setValue("logradouro", resp.endereco.logradouro || "");
          setValue("numero", resp.endereco.numero || "");
          setValue("complemento", resp.endereco.complemento || "");
          setValue("bairro", resp.endereco.bairro || "");
          setValue("cidade", resp.endereco.cidade || "");
          setValue("estado", resp.endereco.estado || "PE");
        }
      })
      .catch((error) => {
        console.error("Erro ao carregar cliente:", error);
        toast.error("Erro ao carregar dados do cliente.");
      });
  }, [id, isEdit, setValue]);

  // Auto-complete de CEP
  useEffect(() => {
    const rawCep = (cepValue || "").replace(/\D/g, "");
    if (rawCep.length === 8) {
      CepService.consultarCep(rawCep)
        .then((resp) => {
          if (resp.data) {
            setValue("logradouro", resp.data.logradouro || "");
            setValue("bairro", resp.data.bairro || "");
            setValue("cidade", resp.data.localidade || "");
            setValue("estado", resp.data.uf || "PE");
          }
        })
        .catch((err) => console.error("Erro no CEP:", err));
    }
  }, [cepValue, setValue]);

 const onSubmit = async (data) => {
  const payload = {
    cnpj: data.cnpj.replace(/\D/g, ""),
    razao_social: data.razaoSocial,
    nome_responsavel: data.nomeResponsavel,
    telefone_contato: (data.telefoneContato || "").replace(/\D/g, ""),
     parceiro: data.parceiro,
    observacao: data.observacao,
    endereco: {
      cep: (data.cep || "").replace(/\D/g, ""),
      logradouro: data.logradouro,
      numero: data.numero,
      complemento: data.complemento,
      bairro: data.bairro,
      cidade: data.cidade,
      estado: data.estado,
    },
  };

  try {
    if (isEdit) {
      await CustomService.patchRequest(`/interno/cliente/${id}`, payload);
      toast.success("Cliente atualizado com sucesso!");
    } else {
      await CustomService.postRequest("/interno/cliente", payload);
      toast.success("Cliente cadastrado com sucesso!");
    }
    reset();
    navigate("/clientes");
  } catch (error) {
  const errorMessage = error?.message || `Erro ao ${isEdit ? "atualizar" : "cadastrar"} cliente.`;

  toast.error(errorMessage);
}
};

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 w-full">
      {/* CNPJ */}
      <div>
        <label className="block mb-1">CNPJ:</label>
        <Controller
          name="cnpj"
          control={control}
          rules={{ required: "O CNPJ é obrigatório" }}
          render={({ field }) => (
            <InputMask
              {...field}
              mask="99.999.999/9999-99"
              disabled={isEdit} //  Desabilita se estiver editando
            >
              {(inputProps) => (
                <input
                  {...inputProps}
                  type="text"
                  className={`border p-2 w-full ${isEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
              )}
            </InputMask>
          )}
        />
        {errors.cnpj && <p className="text-red-500 text-sm">{errors.cnpj.message}</p>}
      </div>

      {/* Razão Social */}
      <div>
        <label className="block mb-1">Razão Social:</label>
        <input
          type="text"
          {...register("razaoSocial", { required: "A razão social é obrigatória" })}
          className="border p-2 w-full"
        />
        {errors.razaoSocial && <p className="text-red-500 text-sm">{errors.razaoSocial.message}</p>}
      </div>

      {/* Nome Responsável */}
      <div>
        <label className="block mb-1">Nome Responsável:</label>
        <input type="text" {...register("nomeResponsavel")} className="border p-2 w-full" />
      </div>

      {/* Telefone */}
      <div>
        <label className="block mb-1">Telefone de Contato:</label>
        <Controller
          name="telefoneContato"
          control={control}
          render={({ field }) => {
            const handleChange = (e) => {
              let value = e.target.value.replace(/\D/g, "");
              if (value.length <= 10) {
                value = value.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
              } else {
                value = value.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
              }
              field.onChange(value);
            };
            return (
              <input
                {...field}
                onChange={handleChange}
                value={field.value}
                type="text"
                className="border p-2 w-full"
              />
            );
          }}
        />
        {errors.telefoneContato && <p className="text-red-500 text-sm">{errors.telefoneContato.message}</p>}
      </div>
      <div className="flex items-center gap-2">
        <label className="block mb-1">Parceiro:</label>
        <Controller
          name="parceiro"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <span
                className="relative flex items-center"
                title="Clientes parceiros podem fazer pedidos pela loja e só depois realizar o pagamento."
              >
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="w-5 h-5"
                />
              </span>
              <span>{field.value ? "Sim" : "Não"}</span>
            </div>
          )}
        />
      </div>
      {/* Observações */}
      <div>
        <label className="block mb-1">Observações:</label>
        <textarea {...register("observacao")} className="border p-2 w-full h-24" />
      </div>

      {/* CEP e Endereço */}
      <div>
        <label className="block mb-1">CEP:</label>
        <Controller
          name="cep"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <InputMask {...field} mask="99999-999">
              {(inputProps) => <input {...inputProps} type="text" className="border p-2 w-full" />}
            </InputMask>
          )}
        />
      </div>
      <div>
        <label className="block mb-1">Logradouro:</label>
        <input type="text" {...register("logradouro")} className="border p-2 w-full" />
      </div>
      <div>
        <label className="block mb-1">Número:</label>
        <input type="text" {...register("numero")} className="border p-2 w-full" />
      </div>
      <div>
        <label className="block mb-1">Complemento:</label>
        <input type="text" {...register("complemento")} className="border p-2 w-full" />
      </div>
      <div>
        <label className="block mb-1">Bairro:</label>
        <input type="text" {...register("bairro")} className="border p-2 w-full" />
      </div>
      <div>
        <label className="block mb-1">Cidade:</label>
        <input type="text" {...register("cidade")} className="border p-2 w-full" />
      </div>
      <div>
        <label className="block mb-1">Estado:</label>
        <select {...register("estado")} className="border p-2 w-full">
          {estadosBR.map((uf) => (
            <option key={uf.value} value={uf.value}>
              {uf.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-end">
        <input
          type="submit"
          value={isEdit ? "Salvar Cliente" : "Cadastrar Cliente"}
          className="bg-blue-default text-white p-3 cursor-pointer rounded"
        />
      </div>
    </form>
  );
}

export default RegisterClienteForm;